import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import StartPillButton from '../UI/Buttons/StartPillButton';
import AuthHeader from '../UI/AuthHeader/AuthHeader';
import AuthSideMenu from '../UI/AuthSideMenu/AuthSideMenu';
import { ButtonSubmitGreen, ButtonThin } from '../UI/Buttons/Buttons.js';
import { useErrorSetter } from '../../hooks/useUiMessages';
import { useSuccessSetter } from '../../hooks/useUiMessages';
import { FaLock } from 'react-icons/fa6';
import { useDispatch } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import { setFetching } from '../../redux/states';
import { FaVideo, FaLightbulb, FaMedal, FaHourglass, FaSuitcase, FaRegBookmark, FaPlay, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { IoMdRemoveCircle } from 'react-icons/io';
import BubbleFloat from '../UI/BubbleFloat/BubbleFloat';
import Connector from './Connector';
import Timeline from './Timeline';
import PhaseDetail from './PhaseDetail';
import ResumePricing from '../Pricing/ResumePricing';
import { Card, CardContent, Typography } from '@mui/material';
import { useTheme } from '../UI/Theme/ThemeContext';

const Container = styled.div`
  padding: 1rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const HorizontalScroller = styled.div`
  display: flex;
  gap: 2.5rem;
  align-items: center;
  padding: 3rem 0;
  overflow-x: auto;
  position: relative;

  @media (max-width: 900px) {
    flex-direction: column;
    overflow-x: hidden;
    gap: 1.25rem;
  }
`;

const ScrollerWrap = styled.div`
  position: relative; /* contains absolute-positioned scroll buttons so they don't move with the scroller content */
`;

const ResponsiveColumns = styled.div`
  display: block;
  @media (max-width:900px) {
    display: flex;
    gap: 12px;
    align-items: stretch; /* make timeline column stretch to match scroller height */
  }
`;

const TimelineHolder = styled.div`
  width: 100%;
  @media (max-width:900px) {
    flex: 0 0 72px; /* thin column for timeline */
    width: 72px;
    margin-left: -12px; /* nudge left so timeline starts earlier */
  }
`;

const ScrollerHolder = styled.div`
  width: 100%;
  @media (max-width:900px) {
    flex: 1 1 auto;
    width: calc(100% - 72px);
  }
`;

const ScrollButton = styled.button`
  position: absolute;
  right: 12px;
  bottom: 12px;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg,#3E8F93,#2a6f6d);
  color: #fff;
  box-shadow: 0 6px 18px rgba(0,0,0,0.12);
  cursor: pointer;
  z-index: 40;

  &:hover { transform: translateX(4px); }
  @media (max-width:900px) { display: none; }
`;

const ScrollButtonLeft = styled(ScrollButton)`
  left: 12px;
  right: auto;
  &:hover { transform: translateX(-4px); }
`;

const PhaseCard = styled(Card)`
  min-width: 320px;
  max-width: 420px;
  border-radius: 12px !important;
  border: 2px solid #5fbec5 !important;
  padding: 0; /* reduced padding to bring content closer to borders */
  position: relative;
  background: transparent !important;
  box-shadow: 0 6px 18px rgba(0,0,0,0.06);
  box-sizing: border-box;

  @media (max-width: 600px) {
    min-width: auto;
    width: 100%;
    max-width: 100%;
  }
`;

const PhaseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const RibbonBadge = styled.div`
  --size: 40px;
  width: var(--size);
  height: var(--size);
  min-width: var(--size);
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg,#ffd24d,#f6b32d);
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0,0,0,0.18), inset 0 -4px 0 rgba(0,0,0,0.06);
  color: #5a3500;
  font-weight: 800;
  font-size: .85rem;
  line-height: 1;
  border: 2px solid rgba(0,0,0,0.06);

  &::after, &::before {
    content: '';
    position: absolute;
    bottom: -12px;
    width: 12px;
    height: 24px;
    background: linear-gradient(180deg,#ffd24d,#f6b32d);
    transform: skewX(-18deg);
    border-radius: 2px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.12);
  }
  &::after { left: 6px; }
  &::before { right: 6px; transform: skewX(18deg); }
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const StepsRow = styled.div`
  display: flex;
  flex-direction: column; /* show steps in a column */
  gap: 0.1rem;
  align-items: stretch;
  margin-top: 0.85rem;
`;

// small pill used for steps to preserve border/shadow but transparent bg
const StepPill = ({ label, icon, color }) => {
  const pillStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    borderRadius: 8,
    boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.03)',
    border: '1px solid rgba(0,0,0,0.06)',
    background: 'transparent',
    color: color || '#222',
    fontSize: '.85rem',
    fontWeight: 600,
    whiteSpace: 'normal'
  };
  const Icon = icon;
  return (
    <div style={pillStyle} aria-hidden>
      {Icon ? <Icon style={{ color: color || 'inherit', width: 14, height: 14 }} /> : null}
      <span style={{ color: color || 'inherit' }}>{label}</span>
    </div>
  )
}

const Footer = styled.div`
  display:flex;
  justify-content: flex-end;
  margin-top: 1.25rem;
`;

const ProgressCircle = ({ percent }) => {
  const size = 64;
  const stroke = 6;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = Math.max(0, circumference - (percent / 100) * circumference);

  let color = '#b88600'; // orange
  if (percent >= 70) color = '#41a956';
  else if (percent >= 40) color = '#cddc39'; // greenish yellow

  return (
    <div style={{ position: 'relative', width: size, height: size }} aria-hidden={true}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={radius} stroke="#eee" strokeWidth={stroke} fill="none" />
        <circle
          cx={size/2}
          cy={size/2}
          r={radius}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ transition: 'stroke-dashoffset 600ms ease, stroke 300ms linear' }}
        />
      </svg>
      <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '.8rem', color }}>
        {percent}%
      </div>
    </div>
  )
}

const CareerRoadmap = () => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const errorSetter = useErrorSetter();
  const successSetter = useSuccessSetter();
  const scrollerRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleSideMenu = () => setMenuOpen(!menuOpen);
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingIndex, setSavingIndex] = useState(-1);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const { themeName } = useTheme();
  const isDarkMode = themeName === 'dark';
  const [mobileTimelineHeight, setMobileTimelineHeight] = useState(0);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [selectedPhaseIndex, setSelectedPhaseIndex] = useState(-1);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [pricingOpen, setPricingOpen] = useState(false);

  // helper to update scroll button visibility (callable from click handlers)
  const updateScrollButtons = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 4;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
    const atStart = el.scrollLeft <= 8;
    setCanScrollRight(hasOverflow && !atEnd);
    setCanScrollLeft(hasOverflow && !atStart);
  };

  // Support multiple possible shapes: `careerRoadmap.phases` or `careerRoadmap.blueprint.phases`
  const phasesArray = (() => {
    if (!roadmap) return [];
    if (Array.isArray(roadmap.phases)) return roadmap.phases;
    if (roadmap.blueprint && Array.isArray(roadmap.blueprint.phases)) return roadmap.blueprint.phases;
    return [];
  })();

  const showEmpty = (!roadmap || phasesArray.length === 0) && !loading;

  // top skills highlights (support blueprint.topSkills or roadmap.topSkills)
  const highlights = (roadmap && (roadmap.blueprint && Array.isArray(roadmap.blueprint.topSkills) ? roadmap.blueprint.topSkills : roadmap.topSkills)) || [];

  useEffect(() => {
    const fetchRoadmap = async () => {
      setLoading(true);
      try { dispatch(setFetching(true)); } catch(_) {}
      try {
        const token = localStorage?.getItem('token');
        const headers = token ? { 'x-access-token': token } : {};
        const resp = await axios.get('/user/career-roadmap', { headers });
        if (resp.data && typeof resp.data.careerRoadmap !== 'undefined' && resp.data.careerRoadmap !== null) {
          setRoadmap(resp.data.careerRoadmap);
        } else {
          setRoadmap(null);
        }
      } catch (err) {
        console.error('Failed to fetch roadmap', err && err.message ? err.message : err);
        setRoadmap(null);
      } finally {
        setLoading(false);
        try { dispatch(setFetching(false)); } catch(_) {}
      }
    }
    fetchRoadmap();
  }, [dispatch]);

  // Exposed refetch function to pass to child modals so they can request a refresh
  const loadRoadmap = async () => {
    try { dispatch(setFetching(true)); } catch(_) {}
    try {
      const token = localStorage?.getItem('token');
      const headers = token ? { 'x-access-token': token } : {};
      const resp = await axios.get('/user/career-roadmap', { headers });
      if (resp.data && typeof resp.data.careerRoadmap !== 'undefined' && resp.data.careerRoadmap !== null) {
        setRoadmap(resp.data.careerRoadmap);
      } else {
        setRoadmap(null);
      }
    } catch (err) {
      console.error('Failed to fetch roadmap', err && err.message ? err.message : err);
    } finally {
      try { dispatch(setFetching(false)); } catch(_) {}
    }
  }

  // monitor scroller overflow and scroll position to show scroll button
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
      try { ro.disconnect(); } catch(e){}
    };
  }, [roadmap]);

  // measure scroller content height for mobile timeline to span full card stack
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => {
      // use scrollHeight so we capture full content
      const h = el.scrollHeight || el.clientHeight || 0;
      setMobileTimelineHeight(h);
    };
    measure();
    const ro2 = new ResizeObserver(measure);
    ro2.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      try { ro2.disconnect(); } catch(e){}
      window.removeEventListener('resize', measure);
    }
  }, [roadmap]);

  const startPhase = async (index) => {
    // Fetch a YouTube course video (maxResults=1) then open modal with result.
    const phase = phasesArray[index];
    const firstStep = Array.isArray(phase?.steps) && phase.steps.length > 0 ? phase.steps[0] : null;
    setSavingIndex(index);

    // If this user already has a chosenVideo persisted for this phase, use it and skip the API call
    try {
      if (phase && phase.chosenVideo) {
        setSelectedVideo(phase.chosenVideo);
        setSelectedPhaseIndex(index);
        setShowPhaseModal(true);
        setSavingIndex(-1);
        return;
      }
    } catch (e) {
      // ignore and continue to fetch
      console.error('error checking existing chosenVideo', e && e.message ? e.message : e);
    }
    try {
      // prepare query using step title first, fallback to phase name
      const query = (firstStep && (firstStep.title || firstStep.name)) ? (firstStep.title || firstStep.name) : (phase.phaseName || '');
      const token = localStorage?.getItem('token');
      const headers = token ? { 'x-access-token': token } : {};

      const resp = await axios.get('/youtube/search', { params: { q: query, maxResults: 1 }, headers });
      if (resp && resp.data && resp.data.ok && Array.isArray(resp.data.items) && resp.data.items.length > 0) {
        const video = resp.data.items[0];
        // persist chosenVideo server-side so each user gets a stable selection
        try {
          const patchResp = await axios.patch(`/user/career-roadmap/phase/${index}`, { chosenVideo: video }, { headers });
          if (patchResp && patchResp.data && patchResp.data.careerRoadmap) setRoadmap(patchResp.data.careerRoadmap);
        } catch (err) {
          console.error('failed to persist chosenVideo', err && err.message ? err.message : err);
          // not fatal — continue to open modal with video
        }

        setSelectedVideo(video);
        setSelectedPhaseIndex(index);
        setShowPhaseModal(true);
      } else {
        // no video returned (fallback handled later) — open modal without video
        setSelectedVideo(null);
        setSelectedPhaseIndex(index);
        setShowPhaseModal(true);
      }
    } catch (err) {
      console.error(err)
      // handle subscription gating
      if (err.response && err.response.status === 403 && err.response.data && err.response.data.message === 'subscription_required') {
        try { errorSetter('Upgrade required to fetch course videos — please upgrade'); } catch(_){}
        setPricingOpen(true);
      } else {
        console.error('Failed to fetch youtube video', err && err.message ? err.message : err);
        try { errorSetter('Failed to start, please try again'); } catch(_){}
      }
    } finally {
      setSavingIndex(-1);
    }
  }

  const renderPhase = (phase, idx) => {
    // derive percent: prefer step-level progress if present, then phase.progress, then fallback
    let percent = 0;
    if (Array.isArray(phase.steps) && phase.steps.length > 0) {
      const stepsWithNumeric = phase.steps.filter(s => typeof s.progress === 'number');
      if (stepsWithNumeric.length > 0) {
        const sum = phase.steps.reduce((acc, s) => acc + (typeof s.progress === 'number' ? Number(s.progress) : 0), 0);
        percent = Math.round(sum / phase.steps.length);
      }
    }
    if (percent === 0 && typeof phase.progress === 'number') percent = Math.round(phase.progress);
    const completed = phase.completed === true;
    if (!percent) {
      if (completed) percent = 100;
      else {
        const prev = phasesArray[idx - 1];
        // when previous phase is complete, newly unlocked phase should start at 0
        if (prev && prev.completed) percent = 0;
      }
    }

    const prevCompleted = phasesArray[idx - 1] && phasesArray[idx - 1].completed;

    const iconMap = {
      course: FaVideo,
      project: FaLightbulb,
      certification: FaMedal,
      examSimulator: FaHourglass,
      internship: FaSuitcase
    };

    const getIconFor = (step) => {
      const TypeIcon = iconMap[step.type] || FaRegBookmark;
      return TypeIcon;
    };

    return (
      <React.Fragment key={idx}>
        <PhaseCard>
          <CardContent>
            <PhaseHeader>
                <div style={{flex:1, display:'flex', alignItems:'flex-start', gap:12}}>
                  <RibbonBadge aria-hidden>{idx + 1}</RibbonBadge>
                  <div style={{display:'flex', flexDirection:'column'}}>
                    <Typography variant="subtitle1" sx={{fontWeight:700,color:'#3E8F93'}}>{phase.phaseName}</Typography>
                    <Typography variant="body2" sx={{mt:0.5,opacity:0.9}}>
                      {phase.description && phase.description.length > 600 ? phase.description.substring(0,600) + '…' : phase.description}
                    </Typography>
                  </div>
                </div>

              <ProgressWrapper>
                <ProgressCircle percent={percent} />
                <div style={{fontSize:'.75rem',fontWeight:700,color:'#b88600'}}>progress</div>
              </ProgressWrapper>
            </PhaseHeader>

            <div style={{ marginTop: '.9rem', fontSize: '.85rem', fontWeight:700, color: isDarkMode ? '#FFF' : '#222' }}>This is what you will learn in this phase:</div>
            <StepsRow>
              {phase.steps.map((s, i) => (
                <div key={i} style={{ display: 'flex', marginTop: 6 }}>
                  <StepPill label={s.title || s.name || s.type} icon={getIconFor(s)} color={'#b88600'} />
                </div>
              ))}
            </StepsRow>

            <Footer>
              { (idx === 0 || prevCompleted) ? (
                <StartPillButton
                  onClick={() => startPhase(idx)}
                  disabled={savingIndex === idx}
                  pending={savingIndex === idx}
                >
                  {completed ? (<><FaPlay style={{ marginRight: 8 }} /> Review Phase</>) : (percent > 0 ? (<><FaPlay style={{ marginRight: 8 }} /> Continue Phase</>) : (<><FaPlay style={{ marginRight: 8 }} /> Start Phase</>))}
                </StartPillButton>
              ) : (
                <ButtonThin onClick={() => errorSetter('Finish previous phases before unlocking this one')} color={'#9e9e9e'} border={'1px solid rgba(0,0,0,0.06)'}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:8 }}><FaLock /> Locked</span>
                </ButtonThin>
              ) }
            </Footer>
          </CardContent>
        </PhaseCard>

        {idx < phasesArray.length - 1 && (
          <Connector index={idx} count={Math.max(1, phasesArray.length - 1)} />
        )}
      </React.Fragment>
    )
  }

  return (
    <div className="auth-container">
      <AuthSideMenu opened={menuOpen} arrayDetails={[]} onSwitch={() => {}} />

      <div className="auth-container-inner">
        <AuthHeader authMenuOpen={menuOpen} onClick={toggleSideMenu} headerText="Career Roadmap" />

        <div style={{ padding: '0 1rem', maxWidth: '1400px', margin: '0 auto', paddingTop: '1rem' }}>
          <Container>
            <Typography variant="h4" sx={{mb:1,fontWeight:700,color:'#3E8F93'}}>
              {(roadmap && roadmap.careerTitle) || ""}
              <span style={{ color: isDarkMode ? '#ffffff' : '#000000', marginLeft: 8, fontWeight: 700 }}>Roadmap</span>
            </Typography>
            <div style={{ marginTop: '.5rem', marginBottom: '1rem', opacity: 0.9, color: isDarkMode ? '#ffffff' : '#000000' }}>{(roadmap && roadmap.motivation) || ""}</div>

            {/* Top Skills Gained (similar to RoadmapPreview) */}
            {highlights && highlights.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: '.85rem', fontWeight: 800, marginBottom: 8, color: isDarkMode ? '#FFD9A8' : '#EE7B1C' }}>TOP SKILLS gained and automatically added to your resume used for applying to jobs for you:</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {highlights.slice(0, 6).map((h, i) => (
                    <div key={i} style={{
                      padding: '6px 10px',
                      borderRadius: 999,
                      fontSize: '.82rem',
                      color: isDarkMode ? '#2b1200' : '#07393a',
                      background: isDarkMode
                        ? 'linear-gradient(90deg, #FF8A00 0%, #FFB86B 100%)'
                        : 'rgba(13,63,66,0.04)'
                    }}>
                      {i === 0 ? '🔑' : i === 1 ? '⭐' : i === 2 ? '🎯' : '⚡'} &nbsp; {String(h).slice(0, 56)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ResponsiveColumns>
              <TimelineHolder>
                {/* timeline that shows journey progress (desktop horizontal, mobile vertical) */}
                <Timeline phases={phasesArray} mobileHeight={mobileTimelineHeight} />
              </TimelineHolder>

              <ScrollerHolder>
                <ScrollerWrap>
                  <HorizontalScroller ref={scrollerRef}>
              {showEmpty ? (
                <div style={{ width: '100%', textAlign: 'center', padding: '3rem 1rem' }}>
                  <Typography variant="h6" sx={{ mb: 1, color: isDarkMode ? '#ffffff' : 'text.primary' }}>No career roadmap found</Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, color: isDarkMode ? '#cfcfcf' : 'text.secondary' }}>You haven't created a career roadmap yet. Create one to get a structured plan and track progress.</Typography>
                  <div style={{ maxWidth: 360, margin: '0 auto' }}>
                    <ButtonSubmitGreen onClick={() => window.location.href = '/user/dashboard/resume'}>Get Career Roadmap</ButtonSubmitGreen>
                  </div>
                </div>
              ) : (
                phasesArray.map((p, i) => renderPhase(p, i))
              )}

                  </HorizontalScroller>

                  {canScrollLeft && (
                    <ScrollButtonLeft
                      title="Scroll left"
                      onClick={() => {
                        const el = scrollerRef.current;
                        if (!el) return;
                        const amount = Math.round(el.clientWidth * 0.7);
                        el.scrollBy({ left: -amount, behavior: 'smooth' });
                        // ensure buttons update after programmatic smooth scroll
                        setCanScrollRight(true);
                        setTimeout(() => updateScrollButtons(), 350);
                      }}
                    >
                      <FaChevronLeft />
                    </ScrollButtonLeft>
                  )}

                  {canScrollRight && (
                    <ScrollButton
                      title="Show more phases"
                      onClick={() => {
                        const el = scrollerRef.current;
                        if (!el) return;
                        const amount = Math.round(el.clientWidth * 0.7);
                        el.scrollBy({ left: amount, behavior: 'smooth' });
                        // reveal left button immediately and update after smooth scroll
                        setCanScrollLeft(true);
                        setTimeout(() => updateScrollButtons(), 350);
                      }}
                    >
                      <FaChevronRight />
                    </ScrollButton>
                  )}

                </ScrollerWrap>
              </ScrollerHolder>
            </ResponsiveColumns>

              {/* Delete roadmap button (hidden when empty) */}
              {!showEmpty && (
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 18 }}>
                  <div style={{ width: 300, textAlign: 'center' }}>
                    <ButtonThin
                      color={'crimson'}
                      border={'1px solid crimson'}
                      bgColor={'transparent'}
                      onClick={async () => {
                        try {
                          await confirm({
                            title: 'Delete Career Roadmap',
                            description: 'Are you sure you want to delete your saved career roadmap?.',
                            confirmationText: 'Delete',
                            cancellationText: 'Cancel'
                          });
                        } catch (e) {
                          // user cancelled
                          return;
                        }

                        // proceed to delete
                        try {
                          dispatch(setFetching(true));
                          const token = localStorage?.getItem('token');
                          const headers = token ? { 'x-access-token': token } : {};
                          const resp = await axios.delete('/user/career-roadmap', { headers });
                            if (resp && resp.data && resp.data.success) {
                              try { localStorage.removeItem('careerRoadmapCache_v1'); } catch(_){}
                              setRoadmap(null);
                              successSetter('Career roadmap deleted');
                            } else {
                              errorSetter('Failed to delete career roadmap');
                            }
                        } catch (err) {
                          console.error('delete roadmap failed', err && err.message ? err.message : err);
                          errorSetter('Failed to delete career roadmap');
                        } finally {
                          try { dispatch(setFetching(false)); } catch(_) {}
                        }
                      }}
                      height="36px"
                      style={{ width: '100%', background: 'transparent', color: 'crimson' }}
                    >
                      <IoMdRemoveCircle style={{ color: 'crimson', fontSize: '.95rem', marginRight: 8 }} />
                      Delete Roadmap
                    </ButtonThin>
                  </div>
                </div>
              )}
          </Container>
        </div>
        {showPhaseModal && selectedPhaseIndex >= 0 && (
          <PhaseDetail phase={phasesArray[selectedPhaseIndex]} phaseIndex={selectedPhaseIndex} video={selectedVideo} onClose={() => { setShowPhaseModal(false); setSelectedPhaseIndex(-1); setSelectedVideo(null); }} onPhaseUpdated={async () => { await loadRoadmap(); }} />
        )}
        {pricingOpen && (
          <ResumePricing open={pricingOpen} closePricing={() => setPricingOpen(false)} />
        )}
        <BubbleFloat />
      </div>
    </div>
  )
}

export default CareerRoadmap;
